import { useState, useEffect } from 'react'
import { Search, Plus, Edit, Trash2, ToggleLeft, ToggleRight, Building2, MapPin, Phone, User, X, ChevronLeft, ChevronRight, Users, Loader2 } from 'lucide-react'
import { hospitalsApi } from '../../lib/api'
import { useAuth } from '../../lib/auth'

const Hospitals = () => {
  const { user } = useAuth()
  const isSupervisor = user?.role === 'medical' && user?.medicalRole === 'supervisor'
  const [hospitals, setHospitals] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState<'add' | 'edit'>('add')
  const [selectedHospital, setSelectedHospital] = useState<any | null>(null)
  const [formLoading, setFormLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  const [formEnabled, setFormEnabled] = useState(true)
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    manager: '',
    remark: '',
    sort_order: 50
  })

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const params: Record<string, unknown> = { page_size: 100 }
        // 机构负责人只看本机构
        if (isSupervisor && user?.hospitalId) {
          params.hospital_id = user.hospitalId
        }
        const resp = await hospitalsApi.list(params) as any
        // hospitals API 返回 {total, hospitals} 格式
        const hospitalList = resp.hospitals || resp.items || resp || []
        if (!Array.isArray(hospitalList)) {
          throw new Error('Invalid response format')
        }
        setHospitals(hospitalList.map((h: any) => ({
          id: h.id, name: h.name, address: h.address || '',
          manager: h.manager || '', phone: h.phone || '',
          status: h.status || 'active', userCount: h.user_count || 0,
          remark: h.remark || '', sort_order: h.sort_order ?? 50,
        })))
      } catch (e) {
        console.error('Failed to load hospitals', e)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const filteredHospitals = hospitals.filter((hospital) => {
    const matchesSearch =
      hospital.name.includes(searchTerm) ||
      hospital.address.includes(searchTerm) ||
      hospital.manager.includes(searchTerm)
    const matchesStatus = statusFilter === 'all' || hospital.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const totalPages = Math.ceil(filteredHospitals.length / pageSize)
  const paginatedHospitals = filteredHospitals.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const handleToggle = async (id: string) => {
    const h = hospitals.find(h => h.id === id)
    if (!h) return
    try {
      await hospitalsApi.update(id, { status: h.status === 'active' ? 'inactive' : 'active' })
      setHospitals(hospitals.map(hospital => hospital.id === id ? { ...hospital, status: hospital.status === 'active' ? 'inactive' : 'active' } : hospital))
    } catch (e) { console.error('Toggle failed', e) }
  }

  const handleEdit = (hospital: any) => {
    setSelectedHospital(hospital)
    setFormData({
      name: hospital.name,
      address: hospital.address || '',
      phone: hospital.phone || '',
      manager: hospital.manager || '',
      remark: hospital.remark || '',
      sort_order: hospital.sort_order ?? 50
    })
    setFormEnabled(hospital.status === 'active')
    setModalType('edit')
    setShowModal(true)
  }

  const handleAdd = () => {
    setSelectedHospital(null)
    setFormData({ name: '', address: '', phone: '', manager: '', remark: '', sort_order: 50 })
    setFormEnabled(true)
    setModalType('add')
    setShowModal(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('确定要删除该机构吗？')) return
    try {
      await hospitalsApi.delete(id)
      setHospitals(hospitals.filter(h => h.id !== id))
    } catch (e) { console.error('Delete failed', e) }
  }

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert('请输入机构名称')
      return
    }
    setFormLoading(true)
    try {
      const payload = {
        name: formData.name,
        address: formData.address,
        phone: formData.phone,
        manager: formData.manager,
        remark: formData.remark,
        status: formEnabled ? 'active' : 'inactive',
        sort_order: Number(formData.sort_order) || 50
      }
      if (modalType === 'add') {
        await hospitalsApi.create(payload)
      } else if (selectedHospital) {
        await hospitalsApi.update(selectedHospital.id, payload)
      }
      // 刷新列表
      const resp = await hospitalsApi.list({ page_size: 100 }) as any
      const hospitalList = resp.hospitals || resp.items || resp || []
      setHospitals(hospitalList.map((h: any) => ({
        id: h.id, name: h.name, address: h.address || '',
        phone: h.phone || '', manager: h.manager || '',
        status: h.status || 'active', userCount: h.user_count || 0,
        remark: h.remark || '', sort_order: h.sort_order ?? 50,
      })))
      setShowModal(false)
    } catch (e) {
      console.error('Save failed', e)
      alert('保存失败，请重试')
    }
    finally { setFormLoading(false) }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center"><Building2 className="w-7 h-7 mr-2 text-green-600" />机构管理</h1>
          <p className="text-gray-500 text-sm mt-1">管理参与积分系统的医疗机构</p>
        </div>
        {!isSupervisor && (
          <button onClick={handleAdd}
            className="flex items-center space-x-2 bg-green-700 text-white px-4 py-2 rounded-xl hover:bg-green-800 transition-colors">
            <Plus className="w-4 h-4" /><span>添加机构</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl shadow-md p-4">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-500">{isSupervisor ? '本机构' : '机构总数'}</p><p className="text-2xl font-bold text-gray-800 mt-1">{hospitals.length}</p></div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center"><Building2 className="w-6 h-6 text-green-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-md p-4">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-500">{isSupervisor ? '本机构状态' : '服务中机构'}</p><p className="text-2xl font-bold text-green-600 mt-1">{hospitals.filter(h => h.status === 'active').length}</p></div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center"><ToggleRight className="w-6 h-6 text-green-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-md p-4">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-500">{isSupervisor ? '本机构医护人员数' : '医护人员数'}</p><p className="text-2xl font-bold text-blue-600 mt-1">{hospitals.reduce((sum, h) => sum + (h.userCount || 0), 0)}</p></div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center"><Users className="w-6 h-6 text-blue-600" /></div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder={isSupervisor ? '仅能查看当前所属机构信息' : '搜索机构名称、地址、负责人...'}
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              readOnly={isSupervisor} />
          </div>
          <div className="flex gap-2">
            {[{ key: 'all', label: '全部' }, { key: 'active', label: '服务中' }, { key: 'inactive', label: '已停用' }].map(f => (
              <button key={f.key} onClick={() => setStatusFilter(f.key)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${statusFilter === f.key ? 'bg-green-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">机构信息</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">联系方式</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">负责人</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">兑换说明</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">医护数</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">状态</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedHospitals.length === 0 ? (
                  <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-400">暂无机构数据</td></tr>
                ) : (
                  paginatedHospitals.map((hospital) => (
                    <tr key={hospital.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><Building2 className="w-5 h-5 text-green-600" /></div>
                          <div>
                            <p className="font-medium text-gray-800">{hospital.name}</p>
                            <p className="text-xs text-gray-500 flex items-center mt-0.5"><MapPin className="w-3 h-3 mr-1" />{hospital.address || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-600 text-sm flex items-center"><Phone className="w-4 h-4 mr-1 text-gray-400" />{hospital.phone || '-'}</p>
                      </td>
                      <td className="px-6 py-4"><p className="text-gray-700">{hospital.manager || '-'}</p></td>
                      <td className="px-6 py-4">
                        <p className="text-xs text-gray-500 max-w-[200px] truncate" title={hospital.remark || ''}>
                          {hospital.remark || '-'}
                        </p>
                      </td>
                      <td className="px-6 py-4"><span className="text-green-600 font-bold">{hospital.userCount || 0}</span><span className="text-gray-400 text-sm"> 人</span></td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${hospital.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {hospital.status === 'active' ? '服务中' : '已停用'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {!isSupervisor && (
                          <div className="flex items-center justify-end space-x-1">
                            <button onClick={() => handleToggle(hospital.id)} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg" title={hospital.status === 'active' ? '停用' : '启用'}>
                              {hospital.status === 'active' ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                            </button>
                            <button onClick={() => handleEdit(hospital)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="编辑">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(hospital.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="删除">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-6 py-4 flex items-center justify-between border-t">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl disabled:opacity-50 hover:bg-gray-200">上一页</button>
            <span className="text-sm text-gray-500">第 {currentPage} / {totalPages} 页</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl disabled:opacity-50 hover:bg-gray-200">下一页</button>
          </div>
        )}
      </div>

      {/* 添加/编辑弹窗 */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-800">{modalType === 'add' ? '添加机构' : '编辑机构'}</h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {/* 机构名称 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">机构名称 <span className="text-red-500">*</span></label>
                <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="请输入机构名称" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              {/* 机构地址 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">机构地址</label>
                <input type="text" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })}
                  placeholder="请输入详细地址" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              {/* 联系电话 + 负责人 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">联系电话</label>
                  <input type="text" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="请输入电话" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">负责人</label>
                  <input type="text" value={formData.manager} onChange={e => setFormData({ ...formData, manager: e.target.value })}
                    placeholder="请输入负责人" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
              {/* 兑换说明 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">兑换说明</label>
                <textarea value={formData.remark} onChange={e => setFormData({ ...formData, remark: e.target.value })}
                  placeholder="请输入兑换说明（如领取时间、领取地点等）" rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" />
              </div>
              {/* 排序 + 启用开关 */}
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center space-x-4">
                  <label className="text-sm font-medium text-gray-700">排序</label>
                  <input type="number" value={formData.sort_order} onChange={e => setFormData({ ...formData, sort_order: Number(e.target.value) || 0 })}
                    className="w-24 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-center" />
                  <span className="text-xs text-gray-400">数值越大越靠前，默认50</span>
                </div>
              </div>
              {/* 启用开关 */}
              <div className="flex items-center space-x-3 pt-2">
                <button type="button" onClick={() => setFormEnabled(!formEnabled)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${formEnabled ? 'bg-green-600' : 'bg-gray-300'}`}>
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formEnabled ? 'left-6' : 'left-0.5'}`} />
                </button>
                <span className="text-sm text-gray-600">{formEnabled ? '已启用该机构' : '已禁用该机构'}</span>
              </div>
              {/* 保存按钮 */}
              <button onClick={handleSave} disabled={formLoading || !formData.name.trim()}
                className={`w-full py-3 rounded-xl font-medium transition-colors ${formLoading || !formData.name.trim() ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-green-700 text-white hover:bg-green-800'}`}>
                {formLoading ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />保存中...</> : (modalType === 'add' ? '添加' : '保存')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Hospitals
